import { useContext, useEffect } from 'react';

import { observer } from 'mobx-react-lite';

import Pagination from 'components/common/Table/Pagination/Pagination';
import ApplicationsTableRow from 'components/applications/ApplicationsPage/ApplicationsTable/ApplicationsTableRow/ApplicationsTableRow';
import Table from 'components/common/Table/Table';
import TableBody from 'components/common/Table/TableBody/TableBody';
import TableHead from 'components/common/Table/TableHead/TableHead';
import TableHeadCell from 'components/common/Table/TableHeadCell/TableHeadCell';
import { RootStoreContext } from 'index';
import { BorrowerApplicationDto } from 'components/applications/applications.dto';
import {
  ApplicationSortingField,
  StatusCategoryTab,
  ApplicationsTableColumnsTitle,
} from 'components/applications/applications.enums';
import ApplicationsTableNoSearchResult from 'components/applications/ApplicationsTableNoSearchResult/ApplicationsTableNoSearchResult';
import {
  APPLICATIONS_TABLE_COLUMNS,
  APPLICATIONS_TABLE_TOOLTIP_TEXT,
  MAX_APPLICATIONS_PER_PAGE,
} from 'components/applications/applications.constants';
import Tooltip from 'components/common/Tooltip/Tooltip';
import { TooltipIcon } from 'components/common/Icons/Icons';
import ApplicationsTableSkeletonRow from 'components/applications/ApplicationsPage/ApplicationsTable/ApplicationsTableSkeletonRow/ApplicationsTableSkeletonRow';

import styles from './ApplicationsTable.module.scss';

function ApplicationsTable() {
  const { applicationsStore, intermediaryApplicationsStore } = useContext(RootStoreContext);
  const { applications, filters, isLoading, totalApplications } = applicationsStore;
  const { companyProfile } = intermediaryApplicationsStore;

  const skeletonRows = Array.from(Array(MAX_APPLICATIONS_PER_PAGE).keys());

  const getInitialData = async () => {
    await applicationsStore.getApplications();
  };

  useEffect(() => {
    if (companyProfile?.masterDealerAgreementWasSigned) {
      getInitialData();
    }
  }, [filters]);

  const updateTableSort = (sortField?: ApplicationSortingField) => () => {
    if (!sortField) {
      return;
    }

    if (filters.sortField === sortField) {
      applicationsStore.updateFilters({ ...filters, sortField: sortField, isSortDesc: !filters.isSortDesc });

      return;
    }

    applicationsStore.updateFilters({ ...filters, sortField: sortField, isSortDesc: true });
  };

  const handlePageCountChange = (pageIndex: number) => {
    applicationsStore.updateFilters({ ...filters, pageIndex: pageIndex });
  };

  const tableRowClass =
    filters.currentTab === StatusCategoryTab.Active ? styles.tableRowTemplate : styles.tableRowTemplateInactive;

  const renderTableContent = () => {
    return !applications.length ? (
      <div className={styles.emptyLine}>You currently have no applications</div>
    ) : (
      <>
        {applications.map((application: BorrowerApplicationDto) => (
          <ApplicationsTableRow key={application.id} application={application} currentTab={filters.currentTab} />
        ))}
      </>
    );
  };

  const renderTableHeader = () => {
    return (
      <TableHead className={tableRowClass}>
        {APPLICATIONS_TABLE_COLUMNS.map((column, index) => (
          <TableHeadCell
            key={index}
            isExist={column.tab ? column.tab === filters.currentTab : true}
            isSorted={!!column.sortField}
            currentSort={filters.sortField === column.sortField}
            isSortedDesc={filters.isSortDesc}
            onClick={updateTableSort(column.sortField)}
          >
            <div className={styles.tableHeaderCell}>
              {column.title}
              {column.title === ApplicationsTableColumnsTitle.StatusName &&
                filters.currentTab === StatusCategoryTab.Active && (
                  <Tooltip title={APPLICATIONS_TABLE_TOOLTIP_TEXT}>
                    <TooltipIcon />
                  </Tooltip>
                )}
            </div>
          </TableHeadCell>
        ))}
      </TableHead>
    );
  };

  if (!applications.length && filters.search) {
    return <ApplicationsTableNoSearchResult searchValue={filters.search} />;
  }

  return (
    <div>
      <Table className={styles.applicationTable}>
        {renderTableHeader()}
        <TableBody>
          {isLoading || !companyProfile?.masterDealerAgreementWasSigned ? (
            <>
              {skeletonRows.map((skeletonRow: number) => (
                <ApplicationsTableSkeletonRow
                  isInactiveApplicationsFilterEnabled={filters.currentTab === StatusCategoryTab.Inactive}
                  key={skeletonRow}
                />
              ))}
            </>
          ) : (
            renderTableContent()
          )}
        </TableBody>
      </Table>
      {!isLoading && !!applications.length && (
        <Pagination
          pageIndex={filters.pageIndex}
          itemsPerPage={MAX_APPLICATIONS_PER_PAGE}
          itemsTotal={totalApplications}
          pagesCount={Math.ceil(totalApplications / MAX_APPLICATIONS_PER_PAGE)}
          onPageIndexChange={handlePageCountChange}
        />
      )}
    </div>
  );
}

export default observer(ApplicationsTable);
